import React, {useCallback, useEffect, useMemo, useState} from "react";
import axios from "config/axiosConfig";
import {useLocation, useNavigate} from "react-router-dom";
import {formatCurrency} from "../util/helpers";

const statusOptions = [
    { value: "REQUESTED", label: "정산 요청" },
    { value: "COMPLETED", label: "정산 완료" },
    { value: "REJECTED", label: "정산 실패" },
];

const displayStatus = (status) => {
    switch (status) {
        case "REQUESTED": return "정산 요청";
        case "COMPLETED": return "정산 완료";
        case "REJECTED": return "정산 실패";
        default: return status || "-";
    }
};

const parseIds = (value) => {
    if (!value) return [];
    try {
        return JSON.parse(value);
    } catch (e) {
        return [];
    }
};
const normalizeStatus = (value) => (value && value.length > 0 ? value : undefined);

export default function WithdrawalSummaryDetail() {
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);

    const memberIdParam = queryParams.get("memberId");
    const walletIdParam = queryParams.get("walletId");
    const statusParam = queryParams.get("status");
    const startDateParam = queryParams.get("startDate");
    const endDateParam = queryParams.get("endDate");

    const stateSummaryRow = location.state?.summaryRow;
    const stateFilters = location.state?.filters;

    const [summaryRow, setSummaryRow] = useState(stateSummaryRow || null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentStatus, setCurrentStatus] = useState(stateSummaryRow?.status || statusParam || "");
    const [filters, setFilters] = useState({
        status: statusParam || stateFilters?.status || "",
        startDate: startDateParam || stateFilters?.startDate || "",
        endDate: endDateParam || stateFilters?.endDate || "",
    });

    const [newStatus, setNewStatus] = useState(stateSummaryRow?.status || statusParam || "");
    const [processedBy, setProcessedBy] = useState("");
    const [memo, setMemo] = useState("");
    const [submitState, setSubmitState] = useState({ loading: false, success: null, error: null });

    // Bank account info for the summarized withdrawals (fetched from the first withdrawal detail)
    const [bankAccount, setBankAccount] = useState(null);

    const withdrawalIds = useMemo(() => parseIds(summaryRow?.withdrawalIds), [summaryRow]);
    const paymentIds = useMemo(() => parseIds(summaryRow?.paymentIds), [summaryRow]);
    const totalAmount = summaryRow?.totalAmount ?? 0;

    const fetchSummaryRow = useCallback(async (targetStatus) => {
        if (!memberIdParam) {
            setSummaryRow(null);
            setError("유효한 회원 ID가 없습니다.");
            return;
        }

        try {
            setLoading(true);
            const params = {};
            const statusToUse = targetStatus ?? filters.status;
            if (statusToUse) params.status = statusToUse;
            if (filters.startDate) params.startDate = filters.startDate;
            if (filters.endDate) params.endDate = filters.endDate;
            const res = await axios.get("/admin/v1/wallet/summarize-withdrawals", { params });
            const list = res.data || [];
            const row = list.find(item =>
                String(item.memberId) === String(memberIdParam) &&
                (walletIdParam ? String(item.walletId) === String(walletIdParam) : true)
            );
            if (row) {
                setSummaryRow(row);
                setCurrentStatus(row.status);
                setNewStatus(row.status);
                setError(null);
            } else {
                setSummaryRow(null);
                setError("해당 조건의 정산 묶음 데이터를 찾을 수 없습니다.");
            }
        } catch (err) {
            setSummaryRow(null);
            setError(err?.response?.data?.detail || "정산 묶음 정보를 불러오는 중 오류가 발생했습니다.");
        } finally {
            setLoading(false);
        }
    }, [memberIdParam, walletIdParam, filters.status, filters.startDate, filters.endDate]);

    useEffect(() => {
        if (stateSummaryRow) {
            setSummaryRow(stateSummaryRow);
            setCurrentStatus(stateSummaryRow.status);
            setNewStatus(stateSummaryRow.status);
            setError(null);
        }
        const initialStatus = stateSummaryRow?.status || filters.status;
        fetchSummaryRow(normalizeStatus(initialStatus));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchSummaryRow(normalizeStatus(currentStatus));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStatus, filters.status, filters.startDate, filters.endDate]);

    // Fetch bank account info from the first withdrawal detail in this summary
    useEffect(() => {
        const loadBankAccount = async () => {
            if (!withdrawalIds || withdrawalIds.length === 0) {
                setBankAccount(null);
                return;
            }
            try {
                const firstId = withdrawalIds[0];
                const res = await axios.get(`/admin/v1/wallet/withdrawals/${firstId}`);
                const data = res.data || {};
                setBankAccount({
                    ownerName: data.accountOwner || null,
                    bankName: data.bankName || null,
                    bankCode: data.bankCode || null,
                    accountNumber: data.accountNumber || null,
                });
            } catch (e) {
                setBankAccount(null);
            }
        };
        loadBankAccount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(withdrawalIds)]);

    const handleStatusSubmit = async (event) => {
        debugger;
        event.preventDefault();
        if (withdrawalIds.length === 0) {
            setSubmitState({ loading: false, success: null, error: "변경할 출금 요청이 없습니다." });
            return;
        }

        try {
            debugger;
            setSubmitState({ loading: true, success: null, error: null });
            await axios.patch("/admin/v1/wallet/summarize-withdrawals/status", {
                withdrawalIds,
                status: newStatus,
                processedBy: processedBy?.trim() || null,
                memo: memo?.trim() || null
            });

            setSubmitState({ loading: false, success: "출금 상태를 변경했습니다.", error: null });
            setFilters(prev => ({ ...prev, status: newStatus }));
            setCurrentStatus(newStatus);

            const params = new URLSearchParams(location.search);
            params.set("status", newStatus);
            navigate(`${location.pathname}?${params.toString()}`, { replace: true });

            fetchSummaryRow(newStatus);
        } catch (err) {
            debugger;
            setSubmitState({
                loading: false,
                success: null,
                error: err?.response?.data?.detail || "출금 상태 변경 중 오류가 발생했습니다."
            });
        }
    };

    const renderSummaryTable = () => {
        if (loading) {
            return <div className="text-center py-4">불러오는 중...</div>;
        }
        if (error) {
            return <div className="alert alert-danger" role="alert">{error}</div>;
        }
        if (!summaryRow) {
            return <div className="text-center py-4">표시할 정산 묶음이 없습니다.</div>;
        }

        return (
            <>
                <table className="table table-bordered">
                    <tbody>
                    <tr>
                        <th style={{width: "200px"}}>회원ID</th>
                        <td>{summaryRow.memberId}</td>
                    </tr>
                    <tr>
                        <th>지갑ID</th>
                        <td>{summaryRow.walletId}</td>
                    </tr>
                    <tr>
                        <th>정산 신청일</th>
                        <td>{summaryRow.requestDate || filters.startDate || '-'}</td>
                    </tr>
                    <tr>
                        <th>정산상태</th>
                        <td>{displayStatus(summaryRow.status)}</td>
                    </tr>
                    <tr>
                        <th>총 금액</th>
                        <td>{totalAmount === 0 ? "0" : formatCurrency(totalAmount)}원</td>
                    </tr>
                    <tr>
                        <th>출금 건수</th>
                        <td>{withdrawalIds.length}</td>
                    </tr>
                    <tr>
                        <th>계좌주</th>
                        <td>{bankAccount?.ownerName || '-'}</td>
                    </tr>
                    <tr>
                        <th>은행</th>
                        <td>{bankAccount?.bankName || '-'} {bankAccount?.bankCode ? `(${bankAccount.bankCode})` : ''}</td>
                    </tr>
                    <tr>
                        <th>계좌번호</th>
                        <td>{bankAccount?.accountNumber || '-'}</td>
                    </tr>
                    <tr>
                        <th>출금 요청 ID</th>
                        <td style={{textAlign: "left"}}>
                            {withdrawalIds.length > 0 ? withdrawalIds.map((wid, index) => (
                                <React.Fragment key={wid}>
                                    <button
                                        type="button"
                                        className="btn btn-link btn-sm p-0 mr-2"
                                        onClick={() => window.open(`/page/payments/withdrawals/detail?id=${wid}`, '_blank', 'noopener')}
                                    >
                                        {wid}
                                    </button>
                                    {index !== withdrawalIds.length - 1 && ", "}
                                </React.Fragment>
                            )) : "-"}
                        </td>
                    </tr>
                    <tr>
                        <th>결제 ID</th>
                        <td style={{textAlign: "left"}}>
                            {paymentIds.length > 0 ? paymentIds.map((pid, index) => (
                                <React.Fragment key={pid}>
                                    <button
                                        type="button"
                                        className="btn btn-link btn-sm p-0 mr-2"
                                        onClick={() => window.open(`/page/payments/detail?id=${pid}`, '_blank', 'noopener')}
                                    >
                                        {pid}
                                    </button>
                                    {index !== paymentIds.length - 1 && ", "}
                                </React.Fragment>
                            )) : "-"}
                        </td>
                    </tr>
                    </tbody>
                </table>
            </>
        );
    };

    return (
        <div className="App">
            <div className="wrapper">
                <div className="content-wrapper" style={{marginLeft: '0px'}}>
                    <div className="content-header">
                        <div className="container-fluid">
                            <div className="row mb-2">
                                <div className="col-sm-12">
                                    <ol className="breadcrumb">
                                        <li className="breadcrumb-item"><a href="/page/payments/withdrawals/summary">정산관리(묶음)</a></li>
                                        <li className="breadcrumb-item active">상세</li>
                                    </ol>
                                </div>
                            </div>
                        </div>
                    </div>

                    <section className="content">
                        <div className="container-fluid">
                            <div className="row">
                                <div className="col-12">
                                    <div className="card">
                                        <div className="card-header d-flex justify-content-between align-items-center">
                                            <h3 className="card-title" style={{fontWeight: 'bold'}}>정산 묶음 상세</h3>
                                            <button
                                                type="button"
                                                className="btn btn-secondary btn-sm"
                                                onClick={() => navigate(-1)}
                                            >
                                                목록으로
                                            </button>
                                        </div>
                                        <div className="card-body">
                                            {renderSummaryTable()}
                                        </div>
                                    </div>

                                    {summaryRow && (
                                        <div className="card">
                                            <div className="card-header">
                                                <h3 className="card-title" style={{fontWeight: 'bold'}}>출금 상태 변경</h3>
                                            </div>
                                            <div className="card-body">
                                                <form onSubmit={handleStatusSubmit}>
                                                    <div className="form-group">
                                                        <label>출금 상태</label>
                                                        <select
                                                            className="form-control"
                                                            value={newStatus}
                                                            onChange={e => setNewStatus(e.target.value)}
                                                            required
                                                        >
                                                            <option value="" disabled>상태를 선택하세요</option>
                                                            {statusOptions.map(option => (
                                                                <option key={option.value} value={option.value}>{option.label}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div className="form-group">
                                                        <label>처리자 (선택)</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={processedBy}
                                                            onChange={e => setProcessedBy(e.target.value)}
                                                            placeholder="처리자 이름 또는 ID를 입력하세요"
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>메모 (선택)</label>
                                                        <textarea
                                                            className="form-control"
                                                            rows={3}
                                                            value={memo}
                                                            onChange={e => setMemo(e.target.value)}
                                                            placeholder="묶음 처리 메모를 입력하세요."
                                                        />
                                                    </div>

                                                    {submitState.error && (
                                                        <div className="alert alert-danger" role="alert">{submitState.error}</div>
                                                    )}
                                                    {submitState.success && (
                                                        <div className="alert alert-success" role="alert">{submitState.success}</div>
                                                    )}

                                                    <button
                                                        type="submit"
                                                        className="btn btn-primary"
                                                        disabled={submitState.loading || !newStatus}
                                                    >
                                                        {submitState.loading ? "변경 중..." : "상태 변경"}
                                                    </button>
                                                </form>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}
